import searchIcon from '@jetbrains/icons/search.svg'
import Button from '@jetbrains/ring-ui/components/button/button'
import Input from '@jetbrains/ring-ui/components/input/input'
import classNames from 'classnames'
import {useContext, memo} from 'react'

import type {BuildId} from '../../../../../../types'
import {cmdOrCtrlPlus} from '../../../../../../utils/keys'
import SvgIcon from '../../../../../common/SvgIcon/SvgIcon'
import {SearchDirection} from '../../../BuildLog.types'
import {ONE_TIME_SEARCH_LIMIT} from '../../BuildLogHeader/BuildLogSearch/BuildLogSearch.actions'
import {useBuildLogSearch} from '../../BuildLogHeader/BuildLogSearch/BuildLogSearch.hooks'
import {isSearchEnabled} from '../../BuildLogHeader/BuildLogSearch/BuildLogSearch.selectors'
import {BuildIdContext} from '../../FullBuildLog.context'

import styles from './BuildLogSearch.css'

import {useAppSelector} from 'src/hooks/react-redux'

type Props = {
  buildId: BuildId
  setSearchActive: (searchActive: boolean) => unknown
}

function BuildLogSearch({setSearchActive, buildId}: Props) {
  const {
    inputRef,
    startId,
    value,
    searchStarted,
    searchDirection,
    notFound,
    limitReached,
    loading,
    nextDisabled,
    previousDisabled,
    searchPrevious,
    onSubmit,
    onResetClick,
    onClear,
    onKeyDown,
    onChange,
  } = useBuildLogSearch(setSearchActive, buildId)

  return (
    <form onSubmit={onSubmit} className={styles.form}>
      <Button className={styles.reset} type="reset" onClick={onResetClick}>
        <SvgIcon icon="chevron-left" />
      </Button>
      <Input
        inputRef={inputRef}
        type="search"
        placeholder={`Search (${cmdOrCtrlPlus}F)`}
        className={styles.inputContainer}
        inputClassName={styles.input}
        icon={searchIcon}
        autoComplete={searchStarted ? 'off' : 'on'}
        value={value}
        onChange={onChange}
        onKeyDown={onKeyDown}
        onClear={onClear}
        autoFocus
      />
      {searchStarted && (notFound || limitReached) && (
        <span
          className={classNames(styles.noResults, {
            [styles.loading]: loading,
          })}
        >{`No\u00a0results${
          limitReached
            ? ` in the\u00a0${
                searchDirection === SearchDirection.PREVIOUS
                  ? 'previous'
                  : startId === 0 || startId == null
                    ? 'first'
                    : 'next'
              } ${ONE_TIME_SEARCH_LIMIT.toLocaleString()}\u00a0lines`
            : ''
        }`}</span>
      )}
      {searchStarted && (!notFound || limitReached) && (
        <>
          <Button
            className={styles.previousNextButton}
            type="button"
            title={`⇧${cmdOrCtrlPlus}G or ⇧F3`}
            onClick={searchPrevious}
            disabled={previousDisabled}
            loader={loading}
          >
            <SvgIcon icon="chevron-up" />
          </Button>
          <Button
            className={styles.previousNextButton}
            type="submit"
            disabled={nextDisabled}
            title={`${cmdOrCtrlPlus}G or F3`}
            primary={false}
            loader={loading}
          >
            <SvgIcon icon="chevron-down" />
          </Button>
        </>
      )}
      {!searchStarted && (
        <Button type="submit" disabled={nextDisabled} primary>
          {'Search'}
        </Button>
      )}
    </form>
  )
}

type WrapperProps = {
  setSearchActive: (searchActive: boolean) => unknown
}

function BuildLogSearchEnabledWrapper({setSearchActive}: WrapperProps) {
  const searchEnabled = useAppSelector(isSearchEnabled)
  const buildId = useContext(BuildIdContext)

  if (!searchEnabled || buildId == null) {
    return null
  }

  return <BuildLogSearch setSearchActive={setSearchActive} buildId={buildId} />
}

export default memo(BuildLogSearchEnabledWrapper)
